import React, { useEffect, useMemo } from "react";
import { css } from "@linaria/core";
import { useIndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import { useIndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import { useIndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import { useSetups, type Setup } from "../../../../api/useSetups";
import { groupBy, indexBy } from "diagnostics-lib/iterable";
import type { Reparability } from "../../../../api/Reparability";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { colors, spacings } from "@ui/Theme";
import InformationLine from "@ui/IconV2/react/InformationLine";
import t from "../../../../i18n";
import { useTranslations } from "../../../../api/useTranslations";
import { fonts } from "@ui";
import { useQuantities } from "../../../../api/useQuantities";
import { notUndefined } from "diagnostics-lib/typescript";
import { findPowerQuantityId } from "diagnostics-domain/aggregationPeriod";
import useActiveAnalysesStream, {
  type ActiveAnalysis,
} from "../../../../api/useActiveAnalysesStream";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import classNames from "classnames";
import { useProject, type Project } from "../../../../api/useProjects";
import formatDate from "@ui/i18n/formatDate";
import Select from "@ui/Select";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import NoDataPlaceholderWidget from "../NoDataPlaceholderWidget";

export const useFinancialImpactSelectedAnalysis = createGlobalState({
  storage: "session",
  key: "useFinancialImpactSelectedAnalysis",
  type: io.union([io.null, io.string]),
  defaultValue: null,
  scoped: true,
});

interface FinancialImpactWidgetProps {
  className?: string;
  projectId: string;
  scope: string; // Optional, used for scoping the global state
}

interface FinancialImpactAnalysisSelectorWidgetProps {
  className?: string;
  project: Project;
  selectedFinancialImpactAnalysisId: string | null;
  setSelectedFinancialImpactAnalysisId: (v: React.SetStateAction<string | null>) => void;
}
interface FinancialImpactContentWidgetProps {
  className?: string;
  project: Project;
  analysis: ActiveAnalysis;
  analysisSetup: Setup;
  analysesOptions: {
    label: string;
    value: string;
  }[];
  setFinancialImpactSelectedAnalysis: (v: React.SetStateAction<string | null>) => void;
}

const PRICE_PER_MWH = 60; // Fixed price in €/MWh

const REPARABILITY_DURATIONS: Record<Reparability, number> = {
  REPARABILITY_0: 3 * 7 * 24, // TEM: 3 weeks
  REPARABILITY_1: 60 * 24, // ATF: 2 months
  REPARABILITY_2: 7000, // AT: 7,000 hours
};

function formatCost(cost: number): string {
  const locale = navigator.language || "fr-FR";
  const isMillion = cost >= 1_000_000;
  const value = isMillion ? cost / 1_000_000 : cost / 1_000;
  const suffix = isMillion ? " M€" : " k€";
  return (
    value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + suffix
  );
}
function calculateFailureCost(items: { impacts: number[]; reparability: Reparability | null }[]) {
  return items.reduce(
    (acc, { impacts, reparability }) => {
      if (!reparability || impacts.length === 0) return acc;
      const cost =
        impacts.reduce((sum, impact) => sum + Math.abs(impact), 0) *
        REPARABILITY_DURATIONS[reparability] *
        PRICE_PER_MWH;
      acc[reparability] += cost;
      return acc;
    },
    { REPARABILITY_0: 0, REPARABILITY_1: 0, REPARABILITY_2: 0 },
  );
}

const FinancialImpactWidget = ({ className, projectId, scope }: FinancialImpactWidgetProps) => {
  const project = useProject(projectId);
  const [selectedFinancialImpactAnalysisId, setSelectedFinancialImpactAnalysisId] =
    useFinancialImpactSelectedAnalysis({
      scope,
    });

  if (!project) return <NoDataPlaceholderWidget />;

  return (
    <FinancialImpactAnalysisSelectorWidget
      className={className}
      project={project}
      selectedFinancialImpactAnalysisId={selectedFinancialImpactAnalysisId}
      setSelectedFinancialImpactAnalysisId={setSelectedFinancialImpactAnalysisId}
    />
  );
};

const FinancialImpactAnalysisSelectorWidget = ({
  className,
  project,
  selectedFinancialImpactAnalysisId,
  setSelectedFinancialImpactAnalysisId,
}: FinancialImpactAnalysisSelectorWidgetProps) => {
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const setups = useSetups(project.id);

  const analysis = useMemo(() => {
    if (!selectedFinancialImpactAnalysisId) return null;
    return activeAnalyses.find(a => a.id === selectedFinancialImpactAnalysisId);
  }, [activeAnalyses, selectedFinancialImpactAnalysisId]);

  const analysisSetup = React.useMemo(
    () => (analysis ? getAnalysisSetup(setups, analysis) : null),
    [setups, analysis],
  );

  const analysesOptions = useMemo(
    () =>
      Array.from(activeAnalyses)
        .sort((a, b) => b.date - a.date)
        .map(a => {
          const cycle = a.group
            ? t("commons.analysis_title.group", { group: `${a.group}`.split("-")[0] ?? "" })
            : "";
          const label = a.label ? t("commons.analysis_title.label", { label: `${a.label}` }) : "";
          return {
            label:
              `${cycle} ${label} ${cycle.length > 0 || label.length > 0 ? "—" : ""} ${formatDate(a.date, "datetime", project.timezone)}`.trim(),
            value: a.id,
          };
        }),
    [activeAnalyses, project.timezone],
  );

  useEffect(() => {
    const selectedAnalysis = analysesOptions[0];
    if (selectedFinancialImpactAnalysisId === null && selectedAnalysis) {
      setSelectedFinancialImpactAnalysisId(selectedAnalysis.value);
    }
  }, [analysesOptions, selectedFinancialImpactAnalysisId, setSelectedFinancialImpactAnalysisId]);

  if (
    !analysisSetup ||
    !analysis ||
    analysesOptions.length === 0 ||
    !selectedFinancialImpactAnalysisId
  )
    return <NoDataPlaceholderWidget />;

  return (
    <FinancialImpactContentWidget
      className={className}
      project={project}
      analysis={analysis}
      analysisSetup={analysisSetup}
      analysesOptions={analysesOptions}
      setFinancialImpactSelectedAnalysis={setSelectedFinancialImpactAnalysisId}
    />
  );
};

const FinancialImpactContentWidget = ({
  className,
  project,
  analysis,
  analysisSetup,
  analysesOptions,
  setFinancialImpactSelectedAnalysis,
}: FinancialImpactContentWidgetProps) => {
  const translations = useTranslations();
  const quantities = useQuantities(project.id);
  const indexedFailureValues = useIndexedFailureValues(project.id);
  const indexedManualFailureValues = useIndexedManualFailureValues(project.id);
  const indexedFailureMetadata = useIndexedFailureMetadata(project.id);

  const failuresByName = React.useMemo(
    () => indexBy(analysisSetup.failures, "name"),
    [analysisSetup],
  );

  const activePowerQuantityId = React.useMemo(() => findPowerQuantityId(quantities), [quantities]);
  const activePowerQuantity = React.useMemo(
    () => quantities.find(q => q.id === activePowerQuantityId),
    [activePowerQuantityId, quantities],
  );

  const confirmedFailuresByDate = React.useMemo(
    () =>
      groupBy(
        indexedFailureMetadata.failureValueStatus.filter(s => s.status === "CONFIRMED"),
        "date",
      ),
    [indexedFailureMetadata.failureValueStatus],
  );

  const confirmedFailures = React.useMemo(
    () => confirmedFailuresByDate.get(analysis.date) ?? [],
    [confirmedFailuresByDate, analysis.date],
  );

  const manualFailureValues = React.useMemo(
    () => indexedManualFailureValues[analysis.id] ?? null,
    [analysis, indexedManualFailureValues],
  );

  const manualFailureItems = React.useMemo(() => {
    if (!manualFailureValues || !activePowerQuantity) return [];

    return Object.values(manualFailureValues).map(mf => ({
      impacts: [mf.impacts[activePowerQuantity.id]].filter(notUndefined),
      reparability: mf.reparability,
    }));
  }, [manualFailureValues, activePowerQuantity]);

  const confirmedFailureItems = React.useMemo(() => {
    if (!activePowerQuantity) return [];

    return confirmedFailures.map(({ date, failureName, reparability }) => {
      const failureValue = indexedFailureValues[date]?.[failureName] ?? null;
      const impacts = [failureValue?.impacts[activePowerQuantity.id]].filter(notUndefined);
      const failure = failuresByName.getOrThrow(failureName);
      const targetReparability = reparability ? reparability : failure.reparability;

      return { impacts, reparability: targetReparability };
    });
  }, [confirmedFailures, indexedFailureValues, failuresByName, activePowerQuantity]);

  const financialByReparability = React.useMemo(
    () => calculateFailureCost([...manualFailureItems, ...confirmedFailureItems]),
    [manualFailureItems, confirmedFailureItems],
  );

  const totalCost = React.useMemo(
    () => Object.values(financialByReparability).reduce((sum, c) => sum + c, 0),
    [financialByReparability],
  );

  return (
    <div className={classNames(financialImpactWidgetContainer, className)}>
      <div className={financialImpactWidgetHeaderRow}>
        <div className={financialImpactWidgetTitleWithIcon}>
          <span className={financialImpactWidgetTitleText}>
            {t("summary.financial_impact_estimation.title")}
          </span>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                text={t("summary.financial_impact_estimation.tooltip", translations)}
              />
            }
          >
            <span className={financialImpactWidgetIconCentered}>
              <InformationLine size="s" color={colors.carbon800} />
            </span>
          </TooltipV2>
        </div>
        <div className={financialImpactWidgetDateWrapper}>
          <Select
            className={analysisSelect}
            prompt=""
            tooltipOverlay={undefined}
            value={analysis.id ?? undefined}
            options={analysesOptions}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFinancialImpactSelectedAnalysis(e.target.value)
            }
            clearable={false}
            filtered
          />
        </div>
      </div>

      <div className={financialImpactWidgetContentSection}>
        <div className={financialImpactWidgetTotalCostRow}>
          <span className={financialImpactWidgetTotalCostValue}>{formatCost(totalCost)}</span>
        </div>

        <div className={financialImpactWidgetCostBreakdown}>
          <div className={financialImpactWidgetCostBreakdownItem}>
            <span className={financialImpactWidgetCostAmount}>
              {formatCost(financialByReparability.REPARABILITY_0)}
            </span>
            <span className={financialImpactWidgetCostDescription}>
              {t("summary.financial_impact_estimation.cost.label", {
                reparability: "REPARABILITY_0",
                translations,
              })}
            </span>
          </div>
          <div className={financialImpactWidgetCostBreakdownItem}>
            <span className={financialImpactWidgetCostAmount}>
              {formatCost(financialByReparability.REPARABILITY_1)}
            </span>
            <span className={financialImpactWidgetCostDescription}>
              {t("summary.financial_impact_estimation.cost.label", {
                reparability: "REPARABILITY_1",
                translations,
              })}
            </span>
          </div>
          <div className={financialImpactWidgetCostBreakdownItem}>
            <span className={financialImpactWidgetCostAmount}>
              {formatCost(financialByReparability.REPARABILITY_2)}
            </span>
            <span className={financialImpactWidgetCostDescription}>
              {t("summary.financial_impact_estimation.cost.label", {
                reparability: "REPARABILITY_2",
                translations,
              })}
            </span>
          </div>
        </div>

        <span className={financialImpactWidgetPriceFootnote}>
          {t("summary.financial_impact_estimation.cost.price")}
        </span>
      </div>
    </div>
  );
};

export default FinancialImpactWidget;

//#region CSS
const analysisSelect = css`
  width: 300px;
`;

const financialImpactWidgetContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  min-width: 350px;
  min-height: 232px;
  padding: ${spacings.m};
  background: ${colors.hydrogen};
  overflow: hidden;
  border-radius: ${spacings.xxs};
  border: 1px solid ${colors.titanium200};
  gap: ${spacings.m};
`;

const financialImpactWidgetIconCentered = css`
  display: inline-flex;
`;

const financialImpactWidgetHeaderRow = css`
  display: flex;
  gap: ${spacings.m};
`;

const financialImpactWidgetTitleWithIcon = css`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  gap: ${spacings.xs};
`;

const financialImpactWidgetTitleText = css`
  color: ${colors.carbon800};
  font: ${fonts.body1SemiBold};
  word-wrap: break-word;
`;

const financialImpactWidgetDateWrapper = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const financialImpactWidgetContentSection = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const financialImpactWidgetTotalCostRow = css`
  display: flex;
  align-items: baseline;
  gap: ${spacings.s};
`;

const financialImpactWidgetTotalCostValue = css`
  color: ${colors.carbon900};
  font: ${fonts.h2};
  word-wrap: break-word;
`;

const financialImpactWidgetCostBreakdown = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.xs};
`;

const financialImpactWidgetCostBreakdownItem = css`
  display: flex;
  align-items: baseline;
  gap: ${spacings.xs};
`;

const financialImpactWidgetCostAmount = css`
  color: ${colors.carbon800};
  font: ${fonts.h4};
  word-wrap: break-word;
`;

const financialImpactWidgetCostDescription = css`
  text-align: right;
  color: ${colors.carbon800};
  font: ${fonts.body1};
  word-wrap: break-word;
`;

const financialImpactWidgetPriceFootnote = css`
  align-self: stretch;
  text-align: right;
  display: flex;
  justify-content: center;
  flex-direction: column;
  color: ${colors.carbon800};
  font: ${fonts.body2};
  word-wrap: break-word;
`;
//#endregion CSS
